import { Alert, Card, InputField, Typography } from '@sonnen/web-ui';
import HoneypotField from '../../../components/HoneypotField/HoneypotField';
import { ACCOUNT_OWNER_MAX_LENGTH } from '../../../constants';
import { TValidationResult } from '../../../hooks/useValidation';
import { useTranslation } from '../../../i18n/i18n';
import { TFormValues, TPaymentDetails } from '../../../types';
import { formatIBAN, formatIbanWithCursorPosition } from '../../../utils/formatting';
import commonStyles from '../../Common.module.css';
import { Checkboxes } from '../Checkboxes/CheckBoxes';

const I18N_SCOPE = 'data.paymentDetails';

type PickedValues = Pick<
  TFormValues,
  | 'iban'
  | 'accountOwner'
  | 'paymentAuthorization'
  | 'termsAndConditions'
  | 'marketing'
  | 'bindingContract'
>;

type PaymentDetailsProps = {
  values: PickedValues;
  onChange: (field: string, value: string | boolean | null) => void;
  validate: (fields?: string[] | undefined) => void;
  errors: TValidationResult<PickedValues>;
  setErrors: (errors: TValidationResult<TPaymentDetails>) => void;
};

export const PaymentDetails = ({
  values,
  onChange,
  validate,
  errors,
  setErrors,
}: PaymentDetailsProps) => {
  const { t } = useTranslation();
  const { accountOwner, iban } = values;

  const onBlurHandler = (fieldName: string) => validate([fieldName]);

  const onChangeHandler = (field: keyof PickedValues | string, value: string | boolean) => {
    setErrors({ ...errors, [field]: '' });

    if (
      field === 'accountOwner' &&
      typeof value === 'string' &&
      value.length > ACCOUNT_OWNER_MAX_LENGTH
    ) {
      setErrors({ ...errors, [field]: t('data.paymentDetails.accountOwnerTooLongError') });
      return;
    }
    onChange(field, value);
  };

  return (
    <Card className={commonStyles.formSection} dataTestId="payment-details">
      <Typography.H3>{t(`${I18N_SCOPE}.sectionTitle`)}</Typography.H3>

      <Alert dataTestId="secure-info" variant="info" title={t(`${I18N_SCOPE}.secureInfoTitle`)}>
        {t(`${I18N_SCOPE}.secureInfo`)}
      </Alert>

      <InputField
        name="iban"
        label={t(`${I18N_SCOPE}.iban`)}
        value={formatIBAN(iban)}
        onChange={(field, value, e) => {
          formatIbanWithCursorPosition(e!);
          onChangeHandler(field, value);
        }}
        onBlur={onBlurHandler}
        error={errors.iban}
        dataTestId="iban"
        maxLength={42}
        expanded
      />

      <InputField
        name="accountOwner"
        label={t(`${I18N_SCOPE}.accountOwner`)}
        sublabel={t(`${I18N_SCOPE}.accountOwnerSublabel`)}
        value={accountOwner}
        onChange={onChangeHandler}
        onBlur={onBlurHandler}
        error={errors.accountOwner}
        dataTestId="account-owner"
        expanded
        maxLength={ACCOUNT_OWNER_MAX_LENGTH}
      />

      <HoneypotField />

      <Checkboxes values={values} onChange={onChangeHandler} errors={errors} />
    </Card>
  );
};
